import { GridColDef } from "@material-ui/data-grid";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { buildAccounts } from "../../accounts";
import { EntityDialog } from "../../components/EntityDialog";
import { ChipsCell, EntityTable } from "../../components/EntityTable";
import { PageHeader } from "../../components/PageHeader";
import { IssueCountChip } from "../../components/SetupIssues";
import { eaterGroupIssues } from "../../setupCheck";
import { useSysadminData } from "../../SysadminData";
import { sysadminPaths } from "../../sysadminUtils";

interface EaterGroupRow {
    id: string;
    eaterGroupName: string;
    officeName: string;
    companyName: string;
    members: number;
    errors: number;
    warnings: number;
    admins: string[];
}

const columns = (showOffice: boolean, showCompany: boolean): GridColDef[] => [
    { field: "eaterGroupName", headerName: "Name", flex: 1, minWidth: 180 },
    { field: "issues", headerName: "Setup", width: 120, sortable: false, renderCell: p => <IssueCountChip errors={(p.row as EaterGroupRow).errors} warnings={(p.row as EaterGroupRow).warnings} /> },
    { field: "members", headerName: "Members", type: "number", width: 150 },
    { field: "officeName", headerName: "Office", flex: 1, minWidth: 160, hide: !showOffice },
    { field: "companyName", headerName: "Company", flex: 1, minWidth: 160, hide: !showCompany },
    {
        field: "admins", headerName: "Admins", flex: 1, minWidth: 220, sortable: false,
        renderCell: p => <ChipsCell values={(p.row as EaterGroupRow).admins} />,
    },
    { field: "id", headerName: "Id", width: 300, hide: true },
];

/** Eater groups of the selected company, or of one office (used on the office page). */
export const EaterGroupsTable: React.FC<{ officeId?: string; title?: string }> = ({ officeId, title }) => {
    const data = useSysadminData();
    const history = useHistory();
    const [creating, setCreating] = useState(false);

    const rows: EaterGroupRow[] = useMemo(() => {
        const accounts = buildAccounts(data);
        return data.eaterGroups
            .filter(({ content }) => (officeId ? content.officeId === officeId : data.matchesSelectedCompany(data.companyIdOfOffice(content.officeId))))
            .map(({ content }) => {
                const office = data.officeById(content.officeId);
                const issues = eaterGroupIssues(data, accounts, content.id);
                return {
                    id: content.id,
                    eaterGroupName: content.eaterGroupName || "Unnamed eater group",
                    officeName: office?.officeName ?? "Unknown office",
                    companyName: data.companyById(office?.companyId)?.companyName ?? "",
                    members: accounts.filter(a => a.user?.eaterGroupId === content.id).length,
                    errors: issues.filter(i => i.severity === "error").length,
                    warnings: issues.filter(i => i.severity === "warning").length,
                    admins: content.eaterGroupAdminIds ?? [],
                };
            })
            .sort((a, b) => a.officeName.localeCompare(b.officeName) || a.eaterGroupName.localeCompare(b.eaterGroupName));
    }, [data, officeId]);

    const selectedName = !officeId && data.selectedCompanyId ? data.companyById(data.selectedCompanyId)?.companyName : undefined;

    return (
        <>
            <EntityTable<EaterGroupRow>
                title={title}
                rows={rows}
                columns={columns(!officeId, !officeId && !data.selectedCompanyId)}
                getRowId={row => row.id}
                searchText={row => [row.eaterGroupName, row.officeName, row.companyName, ...row.admins].join(" ")}
                searchPlaceholder="Search name, office, admin"
                onRowClick={row => history.push(sysadminPaths.detail("eaterGroups", row.id))}
                createLabel="New eater group"
                onCreate={() => setCreating(true)}
                loading={data.loading && data.eaterGroups.length === 0}
                error={data.error}
                onRetry={data.reload}
                emptyText={officeId ? "This office has no eater groups yet" : selectedName ? `${selectedName} has no eater groups yet` : "No eater groups yet"}
            />
            {creating && (
                <EntityDialog
                    target={{ kind: "eaterGroup", officeId }}
                    onClose={() => setCreating(false)}
                    onSaved={id => history.push(sysadminPaths.detail("eaterGroups", id))}
                />
            )}
        </>
    );
};

export const EaterGroups: React.FC = () => {
    const data = useSysadminData();
    const selectedName = data.selectedCompanyId ? data.companyById(data.selectedCompanyId)?.companyName : undefined;
    return (
        <div>
            <PageHeader
                title="Eater groups"
                subtitle={`${selectedName ? `${selectedName}'s eater groups` : "All eater groups"}. Click one to see and add members and check what is missing.`}
            />
            <EaterGroupsTable />
        </div>
    );
};
